import { DateTime } from "luxon"
import { PatientConversationPatientRefDTO } from "@vitable/asclepius-api-client-ts"

const JUST_NOW_THRESHOLD_SECONDS = 60

/** "4 hours ago", or "Just now" inside the first minute (and for clock skew). */
export function formatElapsedSince(isoTimestamp: string, now: DateTime): string {
  const since = DateTime.fromISO(isoTimestamp)
  if (now.diff(since, "seconds").seconds < JUST_NOW_THRESHOLD_SECONDS) return "Just now"
  return since.toRelative({ base: now }) ?? ""
}

/** "Sep 9 10:30pm EDT" in the app's active zone (Luxon's default zone follows the global timezone setter). */
export function formatCreatedAt(isoTimestamp: string): string {
  const created = DateTime.fromISO(isoTimestamp)
  return `${created.toFormat("MMM d h:mm")}${created.toFormat("a").toLowerCase()} ${created.toFormat("ZZZZ")}`
}

/** "DOB 8/14/1982 · Male · IL" — a full year and a label, so the date cannot be misread (Tyler, 2026-10-29). */
export function formatPatientDemographics(patient: PatientConversationPatientRefDTO): string {
  const dateOfBirth = DateTime.fromISO(patient.date_of_birth).toFormat("M/d/yyyy")
  return [`DOB ${dateOfBirth}`, patient.sex, patient.state].join(" · ")
}

/** "Sep 4 · 4:02 PM" beside a message. */
export function formatMessageTime(isoTimestamp: string): string {
  return DateTime.fromISO(isoTimestamp).toFormat("MMM d · h:mm a")
}

/** "12:00pm" for the "Last refreshed" label. */
export function formatClockTime(epochMillis: number): string {
  const at = DateTime.fromMillis(epochMillis)
  return `${at.toFormat("h:mm")}${at.toFormat("a").toLowerCase()}`
}

/** The notice that replaces the composer once a conversation is closed. */
export function formatClosedNotice(closedAt: string): string {
  const closed = DateTime.fromISO(closedAt)
  return `This conversation was closed on ${closed.toFormat("MMM d, yyyy")} at ${closed.toFormat("h:mm a")}.`
}

/** Up to two initials from a display name, skipping a leading "Dr." style title. */
export function nameInitials(fullName: string): string {
  const parts = fullName
    .split(/\s+/)
    .filter((part) => part && !/^(dr|mr|mrs|ms)\.?$/i.test(part))
    .map((part) => part.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean)
  if (!parts.length) return "?"
  const first = parts[0].charAt(0)
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : ""
  return `${first}${last}`.toUpperCase()
}
